import { Injectable, Signal, resource } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AnalyticsInfo, AnalyticsRangeContext } from '@services/analytics-services/core';
import { DateRangePresetType } from '@services/core-services/models';
import { PaymentAnalyticsService, PaymentStats } from '@services/analytics-services/payment';
import { ChartOptions } from '@services/analytics-services/hight-chart-configurations/high-chart-config';
import { STATUS_COLORS } from '@services/analytics-services/hight-chart-configurations';

@Injectable()
export class DashboardPaymentsStore {
  constructor(private readonly paymentAnalyticsService: PaymentAnalyticsService) {}

  public createPaymentAnalyticsResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: ({ params }): Promise<AnalyticsInfo<PaymentStats>> => {
        const { range, compare, interval, preset } = params;
        return firstValueFrom(
          this.paymentAnalyticsService.getAnalytics({
            preset: preset as DateRangePresetType,
            range,
            compare,
            interval,
          })
        );
      },
    });
  }

  /**
   * Creates a resource for loading payment method distribution data
   */
  public createPaymentMethodDistributionResource(context: Signal<AnalyticsRangeContext>) {
    return resource({
      params: () => context(),
      loader: async ({ params }): Promise<ChartOptions | undefined> => {
        const response = await firstValueFrom(
          this.paymentAnalyticsService.getPaymentMethodDistribution(params)
        );

        // Return undefined if no data is available
        if (!response || !response.list || response.list.length === 0) {
          return undefined;
        }

        const chartData = response.list.map((item: any, index: number) => ({
          label: item.payment_method || 'Unknown',
          y: Number(item.payment_count) || 0,
          color:
            Object.values(STATUS_COLORS)[index % Object.values(STATUS_COLORS).length] ||
            STATUS_COLORS['neutral'],
          originalAmount: Number(item.amount) || 0,
        }));

        const totalCount = chartData.reduce((sum: number, item: any) => sum + item.y, 0);

        return {
          data: chartData,
          title: 'Payment Method Distribution',
          showLegend: true,
          legend: {
            enabled: true,
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
          donut: true,
          tooltipFormatter: (point: Highcharts.Point): string => {
            const pointName = point.name || 'Unknown';
            const pointY = point.y;
            const pointPercent = point.percentage ? point.percentage.toFixed(1) : '0.0';
            const pointColor = point.color;

            return `
              <div class="custom-chart-tooltip" style="padding:12px;">
                <div class="d-flex flex-column spacing-8 align-items-start w-100">
                  <h4 class="h4-bold primary-0 text-capitalize" style="margin: 0">${pointName}</h4>
                  <span class="dash-border w-100"></span>
                  <div class="d-flex align-items-center justify-content-between spacing-8 w-100">
                    <div class="d-flex align-items-center spacing-4 w-100 text-center">
                      <div style="background:${pointColor}" class="tooltipDot d-flex align-items-center h4-bold">&nbsp;</div>
                      <div class="h4-reg primary-50">Count (% Total)</div>
                    </div>
                    <div class="h4-bold primary-0">${pointY} (${pointPercent}%)</div>
                  </div>
                </div>
              </div>
            `;
          },
          donutOptions: {
            centerText: {
              text: `${totalCount.toLocaleString()}`,
              subtext: 'Total Payments',
            },
          },
        };
      },
    });
  }
}
